import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Container, Row, Col, Card, Button, Badge, Modal, Image, Form, Alert, Table, InputGroup, ProgressBar } from 'react-bootstrap';
import {
    fetchPedidosLimeira,
    fetchPedidoLimeiraDetalhe,
    atualizarStatusPedidoLimeira,
    createTask,
    fetchUsersForTaskDelegation,
    registrarConferenciaParcial,
    fetchTotaisConferenciaPorItem,
    fetchConferenciasPedido
} from '../../services/api';
import Loading from '../../components/Loading/Loading';

const STATUS_PEDIDO_CONFIG = {
    aberto: { label: 'Aberto', color: 'warning', icon: '📦' },
    parcial: { label: 'Parcial', color: 'info', icon: '📊' },
    enviado: { label: 'Enviado', color: 'primary', icon: '🚚' },
    recebido: { label: 'Recebido', color: 'success', icon: '✅' },
    cancelado: { label: 'Cancelado', color: 'danger', icon: '❌' }
};

// Status de conferência de cada item
const STATUS_CONFERENCIA = {
    pendente: { label: 'Pendente', color: 'secondary', icon: '⏳' },
    ok: { label: 'OK', color: 'success', icon: '✅' },
    duvida: { label: 'Dúvida', color: 'warning', icon: '⚠️' },
    faltou: { label: 'Faltou', color: 'danger', icon: '❌' }
};

const LimeiraConferencia = () => {
    const queryClient = useQueryClient();
    const [filtroStatus, setFiltroStatus] = useState('aberto'); // Por padrão mostra pedidos abertos
    const [showDetalheModal, setShowDetalheModal] = useState(false);
    const [pedidoSelecionado, setPedidoSelecionado] = useState(null);
    const [alert, setAlert] = useState(null);

    // Estado para busca de produto na tela principal (por SKU ou descrição)
    const [buscaProduto, setBuscaProduto] = useState('');
    const [buscaProdutoDebounced, setBuscaProdutoDebounced] = useState('');
    const debounceTimerRef = useRef(null);

    // Debounce da busca de produto (500ms)
    const handleBuscaProdutoChange = useCallback((valor) => {
        setBuscaProduto(valor);
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            setBuscaProdutoDebounced(valor.trim());
        }, 500);
    }, []);

    // Limpar timer ao desmontar
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    // Estado para busca inteligente no modal
    const [buscaItem, setBuscaItem] = useState('');

    // Estado para conferência de itens - { itemId: { status: 'ok'|'faltou'|'mais', qtdRecebida: number, obs: string } }
    const [conferencia, setConferencia] = useState({});

    // Estado para controlar quais itens mostram campo de observação
    const [mostrarObs, setMostrarObs] = useState({});

    // Estado para modal de observação delegada
    const [showObsModal, setShowObsModal] = useState(false);
    const [obsDelegada, setObsDelegada] = useState({ texto: '', usuarioId: '' });
    const [enviandoObs, setEnviandoObs] = useState(false);

    // Estado para histórico de conferências
    const [showHistoricoModal, setShowHistoricoModal] = useState(false);
    const [historicoConferencias, setHistoricoConferencias] = useState([]);

    // Estado para totais recebidos por item (conferências anteriores)
    const [totaisRecebidos, setTotaisRecebidos] = useState({});

    // Query para buscar usuários para delegação
    const { data: usuariosData } = useQuery({
        queryKey: ['usuarios-delegacao'],
        queryFn: fetchUsersForTaskDelegation
    });
    const usuariosDisponiveis = usuariosData?.data || [];

    // Query para buscar pedidos (com busca por produto quando ativo)
    const { data: pedidosData, isLoading, refetch } = useQuery({
        queryKey: ['pedidos-conferencia', filtroStatus, buscaProdutoDebounced],
        queryFn: () => fetchPedidosLimeira({
            status: filtroStatus === 'aberto' ? '' : filtroStatus,
            busca: buscaProdutoDebounced || undefined
        }),
        refetchInterval: buscaProdutoDebounced ? false : 30000 // Desabilitar refetch auto durante busca
    });

    // Query para detalhes do pedido
    const { data: detalheData, isLoading: isLoadingDetalhe } = useQuery({
        queryKey: ['pedido-detalhe-conferencia', pedidoSelecionado?.id],
        queryFn: () => fetchPedidoLimeiraDetalhe(pedidoSelecionado.id),
        enabled: !!pedidoSelecionado?.id
    });

    // Quando detalhes carregam, inicializar estado de conferência
    const itensPedido = detalheData?.data?.itens || [];

    // Filtragem inteligente dos itens por código ou descrição
    const itensFiltrados = itensPedido.filter(item => {
        if (!buscaItem.trim()) return true;
        const termoBusca = buscaItem.toLowerCase().trim();
        const codigo = (item.produto_codigo || '').toLowerCase();
        const descricao = (item.produto_descricao || '').toLowerCase();
        return codigo.includes(termoBusca) || descricao.includes(termoBusca);
    });

    // Carregar totais de conferências anteriores
    useEffect(() => {
        if (pedidoSelecionado?.id) {
            fetchTotaisConferenciaPorItem(pedidoSelecionado.id).then(response => {
                if (response?.success && response?.data?.itens) {
                    const totais = {};
                    response.data.itens.forEach(item => {
                        totais[item.itemId] = item;
                    });
                    setTotaisRecebidos(totais);
                }
            });
        }
    }, [pedidoSelecionado?.id]);

    useEffect(() => {
        if (itensPedido.length > 0 && Object.keys(conferencia).length === 0) {
            const inicial = {};
            itensPedido.forEach(item => {
                // Iniciar com quantidade 0 - só preenche quando usuário marcar OK
                inicial[item.id] = {
                    status: 'pendente',
                    qtdRecebida: 0, // COMEÇA EM 0 - preenche ao marcar OK
                    obs: ''
                };
            });
            setConferencia(inicial);
        }
    }, [itensPedido, totaisRecebidos]);

    // Mutation para confirmar recebimento parcial
    const conferenciaParcialMutation = useMutation({
        mutationFn: ({ pedidoId, dadosConferencia }) =>
            registrarConferenciaParcial(pedidoId, dadosConferencia),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['pedidos-conferencia']);
            queryClient.invalidateQueries(['pedidos-limeira-historico']);

            if (data.data?.novoStatus === 'recebido') {
                setShowDetalheModal(false);
                setPedidoSelecionado(null);
                setConferencia({});
            } else {
                // Recarregar totais para conferência parcial
                fetchTotaisConferenciaPorItem(pedidoSelecionado.id).then(response => {
                    if (response?.success && response?.data?.itens) {
                        const totais = {};
                        response.data.itens.forEach(item => {
                            totais[item.itemId] = item;
                        });
                        setTotaisRecebidos(totais);
                    }
                });
                // Resetar conferência para novos valores
                setConferencia({});
            }

            setAlert({ type: 'success', message: data.message });
            setTimeout(() => setAlert(null), 4000);
        },
        onError: (error) => {
            setAlert({ type: 'danger', message: error.message || 'Erro ao registrar conferência' });
        }
    });

    // Filtrar pedidos para mostrar apenas abertos/parciais
    const pedidos = (pedidosData?.data || []).filter(p => {
        if (filtroStatus === 'aberto') {
            return p.status === 'aberto' || p.status === 'parcial';
        }
        return filtroStatus === '' || p.status === filtroStatus;
    });
    const stats = pedidosData?.stats || {};

    const formatCurrency = (value) => {
        if (!value && value !== 0) return 'R$ -';
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleVerDetalhe = (pedido) => {
        setPedidoSelecionado(pedido);
        setConferencia({}); // Reset conferência ao abrir novo pedido
        setBuscaItem(''); // Reset busca ao abrir novo pedido
        setMostrarObs({}); // Reset observações visíveis
        setTotaisRecebidos({}); // Reset totais
        setShowDetalheModal(true);
    };

    const handleFecharModal = () => {
        setShowDetalheModal(false);
        setPedidoSelecionado(null);
        setConferencia({});
        setMostrarObs({});
        setTotaisRecebidos({});
    };

    // Atualizar status de conferência de um item (toggle - clicar novamente remove)
    const handleStatusItem = (itemId, status) => {
        // Buscar o item para calcular quantidade pendente
        const item = itensPedido.find(i => i.id === itemId);
        const jaRecebido = totaisRecebidos[itemId]?.qtdRecebida || 0;
        const pendente = Math.max(0, (item?.quantidade_solicitada || 0) - jaRecebido);

        setConferencia(prev => {
            const currentStatus = prev[itemId]?.status;
            // Se clicar no mesmo status, volta para pendente (toggle off)
            const newStatus = currentStatus === status ? 'pendente' : status;

            // Se marcou OK e quantidade está 0, preencher automaticamente com quantidade pendente
            let novaQtd = prev[itemId]?.qtdRecebida || 0;
            if (newStatus === 'ok' && novaQtd === 0) {
                novaQtd = pendente;
            }
            // Se desmarcou (voltou para pendente), zerar a quantidade
            if (newStatus === 'pendente') {
                novaQtd = 0;
            }

            return {
                ...prev,
                [itemId]: { ...prev[itemId], status: newStatus, qtdRecebida: novaQtd }
            };
        });
    };

    // Atualizar quantidade recebida de um item
    const handleQtdRecebida = (itemId, qtd) => {
        const qtdNum = parseInt(qtd) || 0;
        setConferencia(prev => ({
            ...prev,
            [itemId]: { ...prev[itemId], qtdRecebida: qtdNum }
        }));
    };

    // Marcar todos como OK (com quantidade pendente)
    const handleMarcarTodosOK = () => {
        const novaConferencia = {};
        itensPedido.forEach(item => {
            const jaRecebido = totaisRecebidos[item.id]?.qtdRecebida || 0;
            const pendente = Math.max(0, (item.quantidade_solicitada || 0) - jaRecebido);
            novaConferencia[item.id] = {
                status: 'ok',
                qtdRecebida: pendente,
                obs: ''
            };
        });
        setConferencia(novaConferencia);
    };

    // Verificar se todos os itens foram conferidos
    const todosConferidos = () => {
        if (itensPedido.length === 0) return false;
        return itensPedido.every(item => conferencia[item.id]?.status && conferencia[item.id]?.status !== 'pendente');
    };

    // Verificar se ao menos 1 item foi conferido (para conferência parcial)
    const algumConferido = () => {
        if (itensPedido.length === 0) return false;
        return itensPedido.some(item => conferencia[item.id]?.status && conferencia[item.id]?.status !== 'pendente');
    };

    // Verificar se itens com problema têm observação
    const itensComProblema = () => {
        return itensPedido.filter(item => {
            const status = conferencia[item.id]?.status;
            return status === 'duvida' || status === 'faltou';
        });
    };

    const todosProblemaTipoObs = () => {
        const problemas = itensComProblema();
        if (problemas.length === 0) return true;
        return problemas.every(item => conferencia[item.id]?.obs?.trim().length > 0);
    };

    // Atualizar observação de um item
    const handleObsItem = (itemId, obs) => {
        setConferencia(prev => ({
            ...prev,
            [itemId]: { ...prev[itemId], obs }
        }));
    };

    // Toggle visibilidade do campo de observação
    const toggleMostrarObs = (itemId) => {
        setMostrarObs(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    // Calcular percentual total recebido até agora (JÁ RECEBIDO - conferências anteriores)
    const calcularPercentualJaRecebido = () => {
        if (itensPedido.length === 0) return 0;

        let totalSolicitado = 0;
        let totalRecebido = 0;

        itensPedido.forEach(item => {
            const qtdSolicitada = item.quantidade_solicitada || 0;
            totalSolicitado += qtdSolicitada;

            const jaRecebido = totaisRecebidos[item.id]?.qtdRecebida || 0;
            totalRecebido += jaRecebido;
        });

        return totalSolicitado > 0 ? Math.round((totalRecebido / totalSolicitado) * 100) : 0;
    };

    // Calcular percentual TOTAL (já recebido + sendo conferido agora) - ATUALIZA EM TEMPO REAL
    const calcularPercentualTotal = () => {
        if (itensPedido.length === 0) return 0;

        let totalSolicitado = 0;
        let totalRecebidoTotal = 0;

        itensPedido.forEach(item => {
            const qtdSolicitada = item.quantidade_solicitada || 0;
            totalSolicitado += qtdSolicitada;

            // Soma: já recebido anteriormente + quantidade sendo conferida agora
            const jaRecebido = totaisRecebidos[item.id]?.qtdRecebida || 0;
            const recebendoAgora = conferencia[item.id]?.qtdRecebida || 0;
            totalRecebidoTotal += jaRecebido + recebendoAgora;
        });

        return totalSolicitado > 0 ? Math.min(100, Math.round((totalRecebidoTotal / totalSolicitado) * 100)) : 0;
    };

    // Calcular percentual desta conferência
    const calcularPercentualConferencia = () => {
        if (itensPedido.length === 0) return 0;

        let totalSolicitado = 0;
        let totalRecebidoAgora = 0;

        itensPedido.forEach(item => {
            totalSolicitado += item.quantidade_solicitada || 0;
            totalRecebidoAgora += conferencia[item.id]?.qtdRecebida || 0;
        });

        return totalSolicitado > 0 ? Math.round((totalRecebidoAgora / totalSolicitado) * 100) : 0;
    };

    // Carregar histórico de conferências
    const handleVerHistorico = async () => {
        if (!pedidoSelecionado?.id) return;
        const response = await fetchConferenciasPedido(pedidoSelecionado.id);
        if (response?.success) {
            setHistoricoConferencias(response.data?.conferencias || []);
            setShowHistoricoModal(true);
        }
    };

    // Enviar observação delegada
    const handleEnviarObsDelegada = async () => {
        if (!obsDelegada.texto.trim()) {
            setAlert({ type: 'danger', message: 'Por favor, informe a observação.' });
            return;
        }

        setEnviandoObs(true);
        try {
            await createTask({
                codigo_produto: pedidoSelecionado?.numero_pedido || 'PEDIDO',
                origem_produto: 'conferencia_limeira',
                observacao: `[Pedido ${pedidoSelecionado?.numero_pedido}] ${obsDelegada.texto}`,
                delegatedToUserId: obsDelegada.usuarioId || null
            });

            setAlert({ type: 'success', message: '✅ Observação enviada com sucesso!' });
            setShowObsModal(false);
            setObsDelegada({ texto: '', usuarioId: '' });
            setTimeout(() => setAlert(null), 4000);
        } catch (error) {
            setAlert({ type: 'danger', message: 'Erro ao enviar observação. Tente novamente.' });
        } finally {
            setEnviandoObs(false);
        }
    };

    // Confirmar conferência (parcial ou total)
    const handleConfirmarConferencia = (finalizarPedido = false) => {
        if (!pedidoSelecionado) return;

        // Preparar dados de conferência para enviar ao backend
        const itensConferidos = itensPedido.map(item => ({
            itemId: item.id,
            produtoCodigo: item.produto_codigo,
            qtdSolicitada: item.quantidade_solicitada,
            qtdRecebida: conferencia[item.id]?.qtdRecebida || 0,
            status: conferencia[item.id]?.status || 'ok',
            obs: conferencia[item.id]?.obs || ''
        }));

        conferenciaParcialMutation.mutate({
            pedidoId: pedidoSelecionado.id,
            dadosConferencia: {
                itens: itensConferidos,
                observacoes: null,
                finalizarPedido
            }
        });
    };

    // Contagem de status (inclui itens já completos de conferências anteriores)
    const contarStatus = () => {
        let ok = 0, faltou = 0, duvida = 0, pendente = 0;
        itensPedido.forEach(item => {
            const jaRecebido = totaisRecebidos[item.id]?.qtdRecebida || 0;
            const qtdSolicitada = item.quantidade_solicitada || 0;
            const completo = jaRecebido >= qtdSolicitada && qtdSolicitada > 0;
            const status = conferencia[item.id]?.status;

            if (completo) {
                ok++; // Item já foi totalmente recebido em conferências anteriores
            } else if (status === 'ok') ok++;
            else if (status === 'faltou') faltou++;
            else if (status === 'duvida') duvida++;
            else pendente++;
        });
        return { ok, faltou, duvida, pendente };
    };

    // Estatísticas
    const renderStats = () => (
        <Row className="mb-4 g-3">
            <Col md={4}>
                <Card className="border-primary border-2 h-100">
                    <Card.Body className="d-flex align-items-center">
                        <div className="rounded-circle bg-primary text-white p-3 me-3" style={{ fontSize: '1.5rem' }}>🚚</div>
                        <div>
                            <h3 className="mb-0">{stats.aberto || 0}</h3>
                            <small className="text-muted">Aberto</small>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={4}>
                <Card className="border-success border-2 h-100">
                    <Card.Body className="d-flex align-items-center">
                        <div className="rounded-circle bg-success text-white p-3 me-3" style={{ fontSize: '1.5rem' }}>✅</div>
                        <div>
                            <h3 className="mb-0">{stats.recebido || 0}</h3>
                            <small className="text-muted">Conferidos</small>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={4}>
                <Card className="border-info border-2 h-100">
                    <Card.Body className="d-flex align-items-center">
                        <div className="rounded-circle bg-info text-white p-3 me-3" style={{ fontSize: '1.5rem' }}>💰</div>
                        <div>
                            <h3 className="mb-0">{formatCurrency(stats.valor_total || 0)}</h3>
                            <small className="text-muted">Valor Total</small>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );

    // Tabela de pedidos
    const renderTabelaPedidos = () => (
        <Card className="shadow">
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                <span>📋 Pedidos para Conferência</span>
                <div className="d-flex gap-2 align-items-center">
                    <Form.Select
                        size="sm"
                        style={{ width: '150px' }}
                        value={filtroStatus}
                        onChange={(e) => setFiltroStatus(e.target.value)}
                    >
                        <option value="aberto">📦 Aberto</option>
                        <option value="recebido">✅ Conferidos</option>
                        <option value="">Todos</option>
                    </Form.Select>
                    <Badge bg="light" text="dark">{pedidos.length}</Badge>
                </div>
            </Card.Header>
            <Card.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {/* 🔍 Busca por Produto (SKU ou Descrição) */}
                <div className="mb-3">
                    <InputGroup>
                        <InputGroup.Text style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: '#fff',
                            border: 'none',
                            fontSize: '1.1rem'
                        }}>
                            🔍
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Buscar produto por SKU ou descrição nos pedidos..."
                            value={buscaProduto}
                            onChange={(e) => handleBuscaProdutoChange(e.target.value)}
                            style={{
                                borderColor: buscaProduto ? '#667eea' : '#ced4da',
                                boxShadow: buscaProduto ? '0 0 0 0.15rem rgba(102, 126, 234, 0.25)' : 'none',
                                fontSize: '0.95rem'
                            }}
                        />
                        {buscaProduto && (
                            <Button
                                variant="outline-secondary"
                                onClick={() => {
                                    setBuscaProduto('');
                                    setBuscaProdutoDebounced('');
                                    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
                                }}
                            >
                                ✕
                            </Button>
                        )}
                    </InputGroup>
                    {buscaProdutoDebounced && (
                        <div className="mt-2 d-flex align-items-center gap-2">
                            <Badge bg="info" className="px-3 py-2" style={{ fontSize: '0.85rem' }}>
                                🔎 Buscando "{buscaProdutoDebounced}" — {pedidos.length} pedido(s) encontrado(s)
                            </Badge>
                            <Button
                                variant="link"
                                size="sm"
                                className="text-decoration-none p-0"
                                onClick={() => {
                                    setBuscaProduto('');
                                    setBuscaProdutoDebounced('');
                                }}
                            >
                                Limpar busca
                            </Button>
                        </div>
                    )}
                </div>

                {pedidos.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        <div style={{ fontSize: '4rem' }}>{buscaProdutoDebounced ? '🔍' : '📭'}</div>
                        <h5 className="mt-3">
                            {buscaProdutoDebounced
                                ? `Nenhum pedido contém "${buscaProdutoDebounced}"`
                                : 'Nenhum pedido encontrado'
                            }
                        </h5>
                        <p className="text-muted">
                            {buscaProdutoDebounced
                                ? 'Tente buscar por outro SKU ou parte da descrição do produto.'
                                : `Não há pedidos ${filtroStatus === 'aberto' ? 'abertos' : filtroStatus === 'recebido' ? 'conferidos' : 'com esse filtro'}.`
                            }
                        </p>
                    </div>
                ) : (
                    <Table responsive hover className="align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Nº Pedido</th>
                                <th>Data Criação</th>
                                <th>Itens</th>
                                <th>Valor Total</th>
                                <th>Status</th>
                                <th>Criado Por</th>
                                <th className="text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pedidos.map(pedido => {
                                const statusConfig = STATUS_PEDIDO_CONFIG[pedido.status] || STATUS_PEDIDO_CONFIG.aberto;
                                const itensMatch = pedido.itens_match || [];
                                return (
                                    <React.Fragment key={pedido.id}>
                                        <tr style={itensMatch.length > 0 ? { borderBottom: 'none' } : {}}>
                                            <td>
                                                <strong className="text-primary">{pedido.numero_pedido}</strong>
                                            </td>
                                            <td>{formatDate(pedido.data_criacao)}</td>
                                            <td>
                                                <Badge bg="dark">{pedido.total_itens || 0}</Badge>
                                            </td>
                                            <td className="text-success fw-bold">
                                                {formatCurrency(pedido.valor_total)}
                                            </td>
                                            <td>
                                                <Badge bg={statusConfig.color}>
                                                    {statusConfig.icon} {statusConfig.label}
                                                </Badge>
                                            </td>
                                            <td>{pedido.usuario_criador_nome || '-'}</td>
                                            <td className="text-center">
                                                <Button
                                                    variant={pedido.status !== 'recebido' ? 'success' : 'outline-primary'}
                                                    size="sm"
                                                    onClick={() => handleVerDetalhe(pedido)}
                                                >
                                                    {pedido.status !== 'recebido' ? '✅ Conferir' : '👁️ Ver Detalhes'}
                                                </Button>
                                            </td>
                                        </tr>
                                        {/* Mostra itens que deram match na busca */}
                                        {itensMatch.length > 0 && (
                                            <tr style={{ borderTop: 'none' }}>
                                                <td colSpan={7} style={{ paddingTop: 0, paddingBottom: '12px' }}>
                                                    <div style={{
                                                        background: 'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%)',
                                                        borderRadius: '8px',
                                                        padding: '8px 14px',
                                                        border: '1px solid rgba(102,126,234,0.2)'
                                                    }}>
                                                        <small className="text-muted d-block mb-1">
                                                            <strong>🎯 Produtos encontrados neste pedido:</strong>
                                                        </small>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {itensMatch.map((item, idx) => (
                                                                <Badge
                                                                    key={idx}
                                                                    bg="light"
                                                                    text="dark"
                                                                    className="d-flex align-items-center gap-1"
                                                                    style={{
                                                                        fontSize: '0.8rem',
                                                                        padding: '6px 10px',
                                                                        border: '1px solid rgba(102,126,234,0.3)',
                                                                        borderRadius: '6px'
                                                                    }}
                                                                >
                                                                    <span className="text-primary fw-bold">#{item.produto_codigo}</span>
                                                                    <span className="text-muted">—</span>
                                                                    <span style={{ maxWidth: '250px' }} className="text-truncate">
                                                                        {item.produto_descricao}
                                                                    </span>
                                                                    <Badge bg="info" pill style={{ fontSize: '0.7rem' }}>
                                                                        Qtd: {item.quantidade_solicitada}
                                                                    </Badge>
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );

    // Contagem de status para exibir no modal
    const statusCount = contarStatus();
    const percentualTotal = calcularPercentualTotal();
    const percentualConferencia = calcularPercentualConferencia();

    return (
        <Container fluid className="p-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="h3 mb-0 text-gray-800">📋 Conferência de Chegada</h1>
                    <p className="mb-0 text-muted">Confira os pedidos que chegaram de Limeira</p>
                </div>
                <Button variant="outline-secondary" onClick={() => refetch()}>
                    🔄 Atualizar
                </Button>
            </div>

            {/* Alert */}
            {alert && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)} className="mb-4">
                    {alert.message}
                </Alert>
            )}

            {/* Loading */}
            {isLoading && <Loading />}

            {/* Stats e Tabela */}
            {!isLoading && (
                <>
                    {renderStats()}
                    {renderTabelaPedidos()}
                </>
            )}

            {/* Modal de Conferência do Pedido */}
            <Modal show={showDetalheModal} onHide={handleFecharModal} size="xl" centered>
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>
                        📦 Conferir Pedido {pedidoSelecionado?.numero_pedido}
                        {pedidoSelecionado?.status === 'parcial' && (
                            <Badge bg="info" className="ms-2">Conferência Parcial</Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoadingDetalhe ? (
                        <Loading />
                    ) : (
                        <>
                            {/* Info do Pedido */}
                            <Row className="mb-3">
                                <Col md={3}>
                                    <small className="text-muted d-block">Data Criação</small>
                                    <strong>{formatDate(pedidoSelecionado?.data_criacao)}</strong>
                                </Col>
                                <Col md={3}>
                                    <small className="text-muted d-block">Valor Total</small>
                                    <strong className="text-success">{formatCurrency(pedidoSelecionado?.valor_total)}</strong>
                                </Col>
                                <Col md={3}>
                                    <small className="text-muted d-block">Status</small>
                                    <Badge bg={STATUS_PEDIDO_CONFIG[pedidoSelecionado?.status]?.color || 'secondary'}>
                                        {STATUS_PEDIDO_CONFIG[pedidoSelecionado?.status]?.label || pedidoSelecionado?.status}
                                    </Badge>
                                </Col>
                                <Col md={3}>
                                    <small className="text-muted d-block">Total Itens</small>
                                    <strong>{itensPedido.length}</strong>
                                </Col>
                            </Row>

                            {/* Barra de Progresso Total - ATUALIZA EM TEMPO REAL */}
                            {pedidoSelecionado?.status !== 'recebido' && (
                                <Card className="mb-3 border-primary" style={{
                                    background: 'linear-gradient(135deg, #1e3a5f 0%, #0d253f 100%)',
                                    border: '2px solid #3498db'
                                }}>
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <div>
                                                <span className="fw-bold text-white" style={{ fontSize: '1.1rem' }}>
                                                    📊 Progresso Total do Pedido
                                                </span>
                                                <small className="text-info d-block">
                                                    Atualiza conforme você vai conferindo os itens
                                                </small>
                                            </div>
                                            <div className="text-end">
                                                <span
                                                    className="fw-bold"
                                                    style={{
                                                        fontSize: '2rem',
                                                        color: percentualTotal >= 100 ? '#2ecc71' : percentualTotal > 50 ? '#3498db' : '#f39c12',
                                                        textShadow: '0 0 10px rgba(255,255,255,0.3)',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    {percentualTotal}%
                                                </span>
                                            </div>
                                        </div>

                                        {/* Barra de Progresso Animada */}
                                        <div style={{
                                            background: 'rgba(255,255,255,0.1)',
                                            borderRadius: '10px',
                                            height: '30px',
                                            overflow: 'hidden',
                                            position: 'relative'
                                        }}>
                                            <div
                                                style={{
                                                    width: `${percentualTotal}%`,
                                                    height: '100%',
                                                    background: percentualTotal >= 100
                                                        ? 'linear-gradient(90deg, #27ae60 0%, #2ecc71 100%)'
                                                        : percentualTotal > 50
                                                            ? 'linear-gradient(90deg, #2980b9 0%, #3498db 100%)'
                                                            : 'linear-gradient(90deg, #e67e22 0%, #f39c12 100%)',
                                                    borderRadius: '10px',
                                                    transition: 'all 0.4s ease-out',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: percentualTotal > 0 ? '0 0 15px rgba(52, 152, 219, 0.5)' : 'none'
                                                }}
                                            >
                                                <span className="text-white fw-bold" style={{ fontSize: '0.9rem', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                                                    {percentualTotal >= 5 && `${percentualTotal}%`}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Contadores de itens */}
                                        <div className="d-flex justify-content-between mt-2" style={{ fontSize: '0.85rem' }}>
                                            <div className="text-white">
                                                <span className="me-3">
                                                    <Badge bg="success" className="me-1">✅ {statusCount.ok}</Badge>
                                                    OK
                                                </span>
                                                <span className="me-3">
                                                    <Badge bg="warning" text="dark" className="me-1">⚠️ {statusCount.duvida}</Badge>
                                                    Dúvida
                                                </span>
                                                <span className="me-3">
                                                    <Badge bg="danger" className="me-1">❌ {statusCount.faltou}</Badge>
                                                    Faltou
                                                </span>
                                                <span>
                                                    <Badge bg="secondary" className="me-1">⏳ {statusCount.pendente}</Badge>
                                                    Pendente
                                                </span>
                                            </div>
                                            <div className="text-info">
                                                {statusCount.ok + statusCount.duvida + statusCount.faltou} / {itensPedido.length} itens conferidos
                                            </div>
                                        </div>

                                        {/* Mensagem de status */}
                                        {percentualTotal >= 100 && (
                                            <Alert variant="success" className="mb-0 mt-2 py-2">
                                                🎉 <strong>100% conferido!</strong> Você pode finalizar o pedido.
                                            </Alert>
                                        )}
                                    </Card.Body>
                                </Card>
                            )}

                            {/* Botões de Ação Rápida */}
                            {pedidoSelecionado?.status !== 'recebido' && (
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={handleMarcarTodosOK}
                                        >
                                            ✅ Marcar Todos OK
                                        </Button>
                                        {calcularPercentualJaRecebido() > 0 && (
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={handleVerHistorico}
                                            >
                                                📜 Ver Histórico de Conferências
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Lista de Itens para Conferência */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Itens do Pedido ({itensPedido.length})</h6>

                                {/* Campo de Busca Inteligente */}
                                <InputGroup style={{ width: '320px' }}>
                                    <InputGroup.Text style={{ background: '#f8f9fa', borderRight: 'none' }}>
                                        🔍
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="Buscar por código ou descrição..."
                                        value={buscaItem}
                                        onChange={(e) => setBuscaItem(e.target.value)}
                                        style={{ borderLeft: 'none' }}
                                    />
                                    {buscaItem && (
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => setBuscaItem('')}
                                            style={{ borderLeft: 'none' }}
                                        >
                                            ✕
                                        </Button>
                                    )}
                                </InputGroup>
                            </div>

                            {/* Indica quantos itens estão sendo exibidos após filtro */}
                            {buscaItem && (
                                <div className="mb-2">
                                    <small className="text-muted">
                                        Mostrando <strong>{itensFiltrados.length}</strong> de <strong>{itensPedido.length}</strong> itens
                                        {itensFiltrados.length === 0 && <span className="text-danger ms-2">— Nenhum item encontrado</span>}
                                    </small>
                                </div>
                            )}

                            {itensPedido.length === 0 ? (
                                <p className="text-muted">Nenhum item encontrado.</p>
                            ) : (
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <Table responsive hover size="sm" className="align-middle mb-0">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th style={{ width: '60px' }}>Img</th>
                                                <th>Código</th>
                                                <th>Descrição</th>
                                                <th className="text-center">Solicitado</th>
                                                <th className="text-center">Já Recebido</th>
                                                {pedidoSelecionado?.status !== 'recebido' && (
                                                    <>
                                                        <th className="text-center" style={{ width: '100px' }}>Receber Agora</th>
                                                        <th className="text-center" style={{ width: '180px' }}>Conferência</th>
                                                    </>
                                                )}
                                                <th className="text-end">Valor</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itensFiltrados.map(item => {
                                                const itemConf = conferencia[item.id] || { status: 'pendente', qtdRecebida: 0 };
                                                const statusConf = STATUS_CONFERENCIA[itemConf.status] || STATUS_CONFERENCIA.pendente;
                                                const jaRecebido = totaisRecebidos[item.id]?.qtdRecebida || 0;
                                                const qtdSolicitada = item.quantidade_solicitada || 0;
                                                const percentItem = qtdSolicitada > 0 ? Math.round(((jaRecebido) / qtdSolicitada) * 100) : 0;
                                                const completo = jaRecebido >= qtdSolicitada;

                                                return (
                                                    <tr
                                                        key={item.id}
                                                        className={
                                                            completo ? 'table-success' :
                                                                itemConf.status === 'ok' ? 'table-success' :
                                                                    itemConf.status === 'faltou' ? 'table-danger' :
                                                                        itemConf.status === 'duvida' ? 'table-warning' : ''
                                                        }
                                                    >
                                                        <td>
                                                            {item.produto_imagem ? (
                                                                <Image
                                                                    src={item.produto_imagem}
                                                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                                    rounded
                                                                />
                                                            ) : (
                                                                <div
                                                                    style={{
                                                                        width: '50px',
                                                                        height: '50px',
                                                                        background: '#f0f0f0',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        borderRadius: '6px',
                                                                        fontSize: '1.25rem'
                                                                    }}
                                                                >
                                                                    📦
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <strong className="text-primary">#{item.produto_codigo}</strong>
                                                        </td>
                                                        <td>
                                                            <div className="text-truncate" style={{ maxWidth: '180px' }} title={item.produto_descricao}>
                                                                {item.produto_descricao}
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="info">Qtd: {qtdSolicitada}</Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="d-flex flex-column align-items-center">
                                                                <Badge bg={completo ? 'success' : jaRecebido > 0 ? 'info' : 'secondary'}>
                                                                    {jaRecebido} / {qtdSolicitada}
                                                                </Badge>
                                                                {jaRecebido > 0 && (
                                                                    <small className={completo ? 'text-success' : 'text-info'}>
                                                                        {percentItem}%
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </td>

                                                        {pedidoSelecionado?.status !== 'recebido' && (
                                                            <>
                                                                <td className="text-center">
                                                                    <Form.Control
                                                                        type="number"
                                                                        size="sm"
                                                                        min="0"
                                                                        max={qtdSolicitada - jaRecebido}
                                                                        value={itemConf.qtdRecebida}
                                                                        onChange={(e) => handleQtdRecebida(item.id, e.target.value)}
                                                                        style={{ width: '80px', margin: '0 auto' }}
                                                                        disabled={completo}
                                                                    />
                                                                </td>
                                                                <td className="text-center">
                                                                    {completo ? (
                                                                        <Badge bg="success">✅ Completo</Badge>
                                                                    ) : (
                                                                        <>
                                                                            <div className="btn-group btn-group-sm" style={{ border: '1px solid #333', borderRadius: '6px' }}>
                                                                                <Button
                                                                                    variant={itemConf.status === 'ok' ? 'success' : 'outline-dark'}
                                                                                    onClick={() => handleStatusItem(item.id, 'ok')}
                                                                                    title="Tudo OK"
                                                                                    style={{ borderColor: '#333' }}
                                                                                >
                                                                                    ✅
                                                                                </Button>
                                                                                <Button
                                                                                    variant={itemConf.status === 'duvida' ? 'warning' : 'outline-dark'}
                                                                                    onClick={() => handleStatusItem(item.id, 'duvida')}
                                                                                    title="Dúvida/Verificar"
                                                                                    style={{ borderColor: '#333' }}
                                                                                >
                                                                                    ⚠️
                                                                                </Button>
                                                                                <Button
                                                                                    variant={itemConf.status === 'faltou' ? 'danger' : 'outline-dark'}
                                                                                    onClick={() => handleStatusItem(item.id, 'faltou')}
                                                                                    title="Faltou"
                                                                                    style={{ borderColor: '#333' }}
                                                                                >
                                                                                    ❌
                                                                                </Button>
                                                                            </div>
                                                                            {/* Botão de Observação */}
                                                                            <Button
                                                                                variant={mostrarObs[item.id] || itemConf.obs ? 'info' : 'outline-secondary'}
                                                                                size="sm"
                                                                                onClick={() => toggleMostrarObs(item.id)}
                                                                                title="Adicionar Observação"
                                                                                className="ms-2"
                                                                                style={{ padding: '2px 8px' }}
                                                                            >
                                                                                📝
                                                                            </Button>
                                                                            {/* Campo de observação */}
                                                                            {(mostrarObs[item.id] || itemConf.status === 'duvida' || itemConf.status === 'faltou') && (
                                                                                <Form.Control
                                                                                    as="textarea"
                                                                                    rows={1}
                                                                                    size="sm"
                                                                                    placeholder={
                                                                                        (itemConf.status === 'duvida' || itemConf.status === 'faltou')
                                                                                            ? "✏️ Descreva o problema (obrigatório)..."
                                                                                            : "✏️ Adicionar observação (opcional)..."
                                                                                    }
                                                                                    value={itemConf.obs || ''}
                                                                                    onChange={(e) => handleObsItem(item.id, e.target.value)}
                                                                                    className={`mt-2 ${(itemConf.status === 'duvida' || itemConf.status === 'faltou') && !itemConf.obs?.trim() ? 'border-danger' : ''}`}
                                                                                    style={{ fontSize: '0.75rem' }}
                                                                                />
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </td>
                                                            </>
                                                        )}

                                                        <td className="text-end text-success fw-bold">
                                                            {formatCurrency(item.valor_total)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleFecharModal}>
                        Fechar
                    </Button>
                    <Button
                        variant="warning"
                        onClick={() => setShowObsModal(true)}
                        title="Registrar observação sobre o pedido"
                    >
                        📝 Observação
                    </Button>
                    {pedidoSelecionado?.status !== 'recebido' && (
                        <>
                            {!todosProblemaTipoObs() && (
                                <span className="text-danger me-3" style={{ fontSize: '0.85rem' }}>
                                    ⚠️ Preencha a observação dos itens com problema
                                </span>
                            )}
                            <Button
                                variant="info"
                                onClick={() => handleConfirmarConferencia(false)}
                                disabled={conferenciaParcialMutation.isPending || !algumConferido() || !todosProblemaTipoObs()}
                                title="Registrar esta conferência e permitir novas entregas"
                            >
                                {conferenciaParcialMutation.isPending ? 'Salvando...' : '📦 Salvar Conferência Parcial'}
                            </Button>
                            <Button
                                variant="success"
                                onClick={() => handleConfirmarConferencia(true)}
                                disabled={conferenciaParcialMutation.isPending || !todosConferidos() || !todosProblemaTipoObs()}
                                title="Finalizar o pedido como totalmente recebido"
                            >
                                {conferenciaParcialMutation.isPending ? 'Finalizando...' : '✅ Finalizar Pedido'}
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Modal de Observação Delegada */}
            <Modal show={showObsModal} onHide={() => setShowObsModal(false)} centered>
                <Modal.Header closeButton className="bg-warning">
                    <Modal.Title>📝 Registrar Observação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-3">
                        Registre uma observação sobre o pedido <strong>{pedidoSelecionado?.numero_pedido}</strong>.
                        Você pode delegar para outro usuário que será notificado.
                    </p>

                    <Form.Group className="mb-3">
                        <Form.Label>Observação *</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Ex: Faltou 2 unidades do produto #BA616, verificar com fornecedor..."
                            value={obsDelegada.texto}
                            onChange={(e) => setObsDelegada(prev => ({ ...prev, texto: e.target.value }))}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Delegar para (opcional)</Form.Label>
                        <Form.Select
                            value={obsDelegada.usuarioId}
                            onChange={(e) => setObsDelegada(prev => ({ ...prev, usuarioId: e.target.value }))}
                        >
                            <option value="">Todos os usuários</option>
                            {usuariosDisponiveis.map(user => (
                                <option key={user.id} value={user.id}>{user.nome}</option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Se deixar em branco, todos os usuários verão a notificação
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowObsModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="warning"
                        onClick={handleEnviarObsDelegada}
                        disabled={enviandoObs || !obsDelegada.texto.trim()}
                    >
                        {enviandoObs ? 'Enviando...' : '📤 Enviar Observação'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Histórico de Conferências */}
            <Modal show={showHistoricoModal} onHide={() => setShowHistoricoModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-info text-white">
                    <Modal.Title>📜 Histórico de Conferências</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {historicoConferencias.length === 0 ? (
                        <p className="text-muted text-center py-4">Nenhuma conferência anterior registrada.</p>
                    ) : (
                        <Table responsive striped>
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Usuário</th>
                                    <th className="text-center">% Conferido</th>
                                    <th className="text-center">Itens OK</th>
                                    <th className="text-center">Faltou</th>
                                    <th className="text-center">Dúvida</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historicoConferencias.map((conf, idx) => (
                                    <tr key={conf.id || idx}>
                                        <td>{formatDate(conf.data_conferencia)}</td>
                                        <td>{conf.usuario_nome || '-'}</td>
                                        <td className="text-center">
                                            <Badge bg="primary">{conf.percentual_conferido}%</Badge>
                                        </td>
                                        <td className="text-center">
                                            <Badge bg="success">{conf.itens_ok || 0}</Badge>
                                        </td>
                                        <td className="text-center">
                                            <Badge bg="danger">{conf.itens_faltou || 0}</Badge>
                                        </td>
                                        <td className="text-center">
                                            <Badge bg="warning" text="dark">{conf.itens_duvida || 0}</Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowHistoricoModal(false)}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default LimeiraConferencia;
